import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, Input } from "@/design-system";
import { Icon } from "@/components/Icon";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ApiRequestError, api } from "@/api/client";
// Bundler import (Vite inlines the asset) — same rationale as AuthScreen
// (issue #106): no runtime network fetch for the logo.
import logoMark from "@/design-system/assets/logo-mark.svg";

/**
 * Forgot password — requests a reset link for an account.
 *
 * `POST /api/password-resets` returns `204` whether or not the account
 * exists (anti-enumeration, issue #151), so this screen shows the same
 * generic success copy in every non-error case: the only failures worth
 * surfacing are real client/network/server errors.
 */
export function ForgotPasswordScreen() {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestMutation = useMutation<void, Error, { email: string }>({
    mutationFn: (body) =>
      api.post("/api/password-resets", body, { token: null }),
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (requestMutation.isPending) return;

    setError(null);

    try {
      await requestMutation.mutateAsync({ email });
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : t("forgotPassword.errorGeneric"),
      );
    }
  }

  return (
    <div
      style={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6) var(--space-4)",
      }}
    >
      <Card padding="var(--space-6)" style={{ width: "100%", maxWidth: 420 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: "var(--space-5)",
          }}
        >
          <img
            src={logoMark}
            alt=""
            width={44}
            height={44}
            style={{ flex: "none" }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                font: "var(--fw-extra) var(--fs-2xl)/1 var(--font-display)",
                color: "var(--text-strong)",
                letterSpacing: "var(--ls-tight)",
              }}
            >
              TheCore Studio
            </div>
            <div
              style={{
                font: "var(--fw-medium) var(--fs-sm)/1 var(--font-body)",
                color: "var(--text-faint)",
                marginTop: 4,
              }}
            >
              {t("auth.tagline")}
            </div>
          </div>
          <LanguageSwitcher />
        </div>

        <div
          style={{
            font: "var(--fw-extra) var(--fs-xl)/1.2 var(--font-display)",
            color: "var(--text-strong)",
            letterSpacing: "var(--ls-tight)",
            marginBottom: 4,
          }}
        >
          {t("forgotPassword.title")}
        </div>
        <div
          style={{
            font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
            color: "var(--text-muted)",
            marginBottom: "var(--space-5)",
          }}
        >
          {t("forgotPassword.subtitle")}
        </div>

        {sent ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <Card
              role="status"
              padding="10px 14px"
              style={{
                background: "var(--status-success-bg)",
                border: "1px solid var(--sage-300)",
                boxShadow: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  color: "var(--status-success-fg)",
                  font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
                }}
              >
                <span style={{ display: "inline-flex", color: "var(--sage-600)" }}>
                  <Icon name="check" size={18} />
                </span>
                <div>
                  <div style={{ font: "var(--fw-extra) var(--fs-sm)/1.4 var(--font-body)" }}>
                    {t("forgotPassword.successTitle")}
                  </div>
                  <div style={{ font: "var(--fw-regular) var(--fs-sm)/1.4 var(--font-body)" }}>
                    {t("forgotPassword.successBody")}
                  </div>
                </div>
              </div>
            </Card>
            <Link
              to="/login"
              id="back-to-login-link"
              style={{
                font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
                color: "var(--brand)",
                textDecoration: "none",
                alignSelf: "flex-start",
              }}
            >
              ← {t("forgotPassword.backToLogin")}
            </Link>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            noValidate
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-4)",
            }}
          >
            <Input
              label={t("auth.emailLabel")}
              type="email"
              name="email"
              autoComplete="email"
              placeholder={t("auth.emailPlaceholder")}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              required
              autoFocus
              disabled={requestMutation.isPending}
              iconLeft={<Icon name="user" size={18} />}
            />

            {error && (
              <Card
                role="alert"
                padding="10px 14px"
                style={{
                  background: "var(--status-danger-bg)",
                  border: "1px solid var(--rust-500)",
                  boxShadow: "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    color: "var(--status-danger-fg)",
                    font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
                  }}
                >
                  <span style={{ display: "inline-flex", color: "var(--rust-500)" }}>
                    <Icon name="flame" size={18} />
                  </span>
                  {error}
                </div>
              </Card>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={requestMutation.isPending}
            >
              {requestMutation.isPending
                ? t("forgotPassword.submitting")
                : t("forgotPassword.submit")}
            </Button>

            <Link
              to="/login"
              id="back-to-login-link"
              style={{
                font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
                color: "var(--brand)",
                textDecoration: "none",
                alignSelf: "flex-start",
              }}
            >
              ← {t("forgotPassword.backToLogin")}
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}